import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useAppContext } from '@/hooks/useAppContext';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, X, ImagePlus } from 'lucide-react';

export function CreateFundraiserForm() {
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const { mutate: createEvent, isPending: isPublishing } = useNostrPublish();
  const { mutateAsync: uploadFile, isPending: isUploading } = useUploadFile();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    goal: '',
    image: '',
    hashtags: '',
    closedAt: '',
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast({
        title: 'Login required',
        description: 'You must be logged in to create a fundraiser.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.title || !formData.goal || !formData.content) {
      toast({
        title: 'Missing required fields',
        description: 'Please fill in title, goal, and description.',
        variant: 'destructive',
      });
      return;
    }

    const goalSats = parseInt(formData.goal);
    if (isNaN(goalSats) || goalSats <= 0) {
      toast({
        title: 'Invalid goal',
        description: 'Goal must be a positive number.',
        variant: 'destructive',
      });
      return;
    }

    try {
      let imageUrl = formData.image;
      if (imageFile) {
        toast({
          title: 'Uploading image...',
          description: 'Please wait while we upload your image.',
        });
        const tags = await uploadFile(imageFile);
        imageUrl = tags[0][1];
      }

      // NIP-75 requires amount in MILLISATS
      const goalMillisats = goalSats * 1000;

      const tags: string[][] = [
        ['amount', goalMillisats.toString()],
        ['relays', ...config.relayMetadata.relays.map(r => r.url)],
      ];

      if (formData.summary) {
        tags.push(['summary', formData.summary]);
      }

      if (imageUrl) {
        tags.push(['image', imageUrl]);
      }

      if (formData.closedAt) {
        const closedAtTimestamp = Math.floor(new Date(formData.closedAt).getTime() / 1000);
        tags.push(['closed_at', closedAtTimestamp.toString()]);
      }

      const hashtags = formData.hashtags
        .split(',')
        .map(t => t.trim())
        .filter(Boolean);

      hashtags.forEach(tag => {
        tags.push(['t', tag]);
      });

      // NIP-75: content is the description, title goes in first line
      const fullContent = formData.title + '\n\n' + formData.content;

      createEvent({
        kind: 9041, // NIP-75 Zap Goal
        content: fullContent,
        tags,
      }, {
        onSuccess: (event) => {
          const currentDomain = window.location.origin;
          const shareUrl = `${currentDomain}?fund=${event.id}`;
          
          toast({
            title: 'Campaign created!',
            description: `Share: ${shareUrl}`,
          });

          navigate(`/goal/${event.id}`);
        },
        onError: (error) => {
          toast({
            title: 'Failed to create fundraiser',
            description: error.message,
            variant: 'destructive',
          });
        },
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    }
  };

  if (!user) {
    return (
      <Card className="border-2 border-orange-500/20 bg-white shadow-xl">
        <CardContent className="py-20 px-10 text-center">
          <h3 className="text-3xl font-black text-black mb-4">Login Required</h3>
          <p className="text-xl text-gray-700 font-semibold">
            You must be logged in to create a fundraiser.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-orange-500/20 bg-white shadow-2xl">
      <CardContent className="p-12">
        <form onSubmit={handleSubmit} className="space-y-10">
          {/* Campaign Title */}
          <div className="space-y-4">
            <Label htmlFor="title" className="text-black text-2xl font-black">
              Campaign Title <span className="text-orange-600">*</span>
            </Label>
            <Input
              id="title"
              placeholder="e.g., Fund the Next Nostr Client"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
              className="bg-orange-50/50 border-2 border-orange-500/30 text-black placeholder:text-gray-500 focus:border-orange-600 focus:ring-4 focus:ring-orange-500/20 h-16 text-xl font-bold px-6"
            />
            <p className="text-base text-gray-600 leading-relaxed font-medium">
              Make it compelling and clear what you're building
            </p>
          </div>

          {/* Short Summary */}
          <div className="space-y-4">
            <Label htmlFor="summary" className="text-black text-2xl font-black">
              Short Summary
            </Label>
            <Input
              id="summary"
              placeholder="One compelling sentence about your project"
              value={formData.summary}
              onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
              className="bg-orange-50/50 border-2 border-orange-500/30 text-black placeholder:text-gray-500 focus:border-orange-600 focus:ring-4 focus:ring-orange-500/20 h-16 text-lg px-6"
            />
            <p className="text-base text-gray-600 leading-relaxed font-medium">
              A brief tagline that appears in campaign previews
            </p>
          </div>

          {/* Funding Goal */}
          <div className="space-y-4">
            <Label htmlFor="goal" className="text-black text-2xl font-black">
              Funding Goal <span className="text-orange-600">*</span>
            </Label>
            <div className="relative">
              <Input
                id="goal"
                type="number"
                placeholder="1000000"
                value={formData.goal}
                onChange={(e) => setFormData(prev => ({ ...prev, goal: e.target.value }))}
                required
                className="bg-orange-50/50 border-2 border-orange-500/30 text-black placeholder:text-gray-500 focus:border-orange-600 focus:ring-4 focus:ring-orange-500/20 h-16 text-3xl font-black pr-24 px-6"
              />
              <div className="absolute right-6 top-1/2 -translate-y-1/2 text-orange-600 font-black text-xl">
                sats
              </div>
            </div>
            <p className="text-base text-gray-600 leading-relaxed font-medium">
              How many satoshis do you need to raise?
            </p>
          </div>

          {/* Campaign Image */}
          <div className="space-y-4">
            <Label className="text-black text-2xl font-black">Campaign Image</Label>
            {imagePreview || formData.image ? (
              <div className="relative rounded-3xl overflow-hidden border-2 border-orange-500/30 group">
                <img
                  src={imagePreview || formData.image}
                  alt="Preview"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center" />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-4 right-4 h-12 w-12 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-xl"
                  onClick={handleRemoveImage}
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
            ) : (
              <div className="border-3 border-dashed border-orange-500/40 rounded-3xl p-20 text-center hover:border-orange-600 transition-all duration-300 bg-orange-50/30 group cursor-pointer">
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageSelect}
                  disabled={isUploading}
                />
                <label htmlFor="image" className="cursor-pointer">
                  <ImagePlus className="h-20 w-20 mx-auto mb-5 text-orange-400 group-hover:text-orange-600 transition-colors duration-300 drop-shadow-lg" />
                  <p className="text-xl text-black mb-2 font-bold">Click to upload campaign image</p>
                  <p className="text-base text-gray-600 font-semibold">Recommended: 1200x630px, PNG or JPG, max 10MB</p>
                </label>
              </div>
            )}
          </div>

          {/* Full Description */}
          <div className="space-y-4">
            <Label htmlFor="content" className="text-black text-2xl font-black">
              Full Description <span className="text-orange-600">*</span>
            </Label>
            <Textarea
              id="content"
              placeholder="Tell your story...&#10;&#10;What are you building?&#10;Why does it matter?&#10;How will the funds be used?&#10;What milestones do you have planned?"
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              required
              rows={14}
              className="bg-orange-50/50 border-2 border-orange-500/30 text-black placeholder:text-gray-500 focus:border-orange-600 focus:ring-4 focus:ring-orange-500/20 resize-none text-lg leading-relaxed font-medium px-6 py-4"
            />
            <p className="text-base text-gray-600 leading-relaxed font-medium">
              Paint a clear picture of your vision. Be specific about how you'll use the funds.
            </p>
          </div>

          {/* Optional Fields */}
          <div className="grid sm:grid-cols-2 gap-8 pt-8 border-t-2 border-orange-500/20">
            <div className="space-y-4">
              <Label htmlFor="closedAt" className="text-black text-lg font-black">
                End Date (Optional)
              </Label>
              <Input
                id="closedAt"
                type="datetime-local"
                value={formData.closedAt}
                onChange={(e) => setFormData(prev => ({ ...prev, closedAt: e.target.value }))}
                className="bg-orange-50/50 border-2 border-orange-500/30 text-black focus:border-orange-600 h-14 font-medium px-6"
              />
            </div>

            <div className="space-y-4">
              <Label htmlFor="hashtags" className="text-black text-lg font-black">
                Tags
              </Label>
              <Input
                id="hashtags"
                placeholder="nostr, bitcoin, opensource"
                value={formData.hashtags}
                onChange={(e) => setFormData(prev => ({ ...prev, hashtags: e.target.value }))}
                className="bg-orange-50/50 border-2 border-orange-500/30 text-black placeholder:text-gray-500 focus:border-orange-600 h-14 text-base font-medium px-6"
              />
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isPublishing || isUploading}
            className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white h-20 text-2xl font-black shadow-2xl shadow-orange-500/40 hover:shadow-orange-500/60 transition-all duration-300 mt-10"
          >
            {isPublishing || isUploading ? (
              <>
                <Loader2 className="mr-3 h-7 w-7 animate-spin" />
                {isUploading ? 'Uploading Image...' : 'Publishing Campaign...'}
              </>
            ) : (
              'Launch Your Campaign'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
